import { GA4Event, Rank } from '@jusbrasil-web/shared-ga4-events'
import { RefCallback, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useGA4Enricher, useHasGA4Enricher } from './ga4-enricher-context'
import { type GA4Dispatcher, useGA4Dispatcher } from './use-ga4-dispatcher'
import { setIfDefined, setPanelParameters } from './utils'

const INTERSECTION_THRESHOLD = 0.1
const IS_DEV = process.env.NODE_ENV !== 'production'

type Parameters = {
  componentClass?: string
  componentRank?: Rank
  resourceId?: string
}

type Options = {
  parameters?: Parameters
  trackView?: boolean
}

type GA4ComponentDispatcher = {
  /** Callback ref to attach to the tracked element so its view can be reported. */
  ref: RefCallback<HTMLElement>
  /** Dispatches a GA4 event enriched with the component and panel parameters. */
  send: GA4Dispatcher['sendGA4Event']
}

export function useGA4ComponentDispatcher(
  componentName: string,
  options?: Options
): GA4ComponentDispatcher {
  const { parameters, trackView } = options ?? {}
  const { componentClass, componentRank, resourceId } = parameters ?? {}
  const { sendGA4Event } = useGA4Dispatcher()
  const { ga4Parameters, addViewedComponent } = useGA4Enricher()
  const hasEnricher = useHasGA4Enricher()

  if (trackView && !hasEnricher && IS_DEV) {
    throw new Error(
      `[shared-ga4] useGA4ComponentDispatcher: trackView is enabled for "${componentName}" but no ` +
        '<GA4EnricherProvider> is mounted as an ancestor. Without it, viewed components are never reported.'
    )
  }

  const [trackedComponent, setTrackedComponent] = useState<HTMLElement | null>(null)
  const intersectionObserverRef = useRef<IntersectionObserver | null>(null)
  // Tracks whether the callback ref was ever attached to a node. Set synchronously
  // during commit, so it's reliable by the time the effect runs — unlike the
  // `trackedComponent` state, which only reflects the node after a re-render.
  const hasAttachedRef = useRef(false)
  const ref: RefCallback<HTMLElement> = useRef((node: HTMLElement | null) => {
    hasAttachedRef.current = node != null
    setTrackedComponent(node)
  }).current

  useEffect(() => {
    if (trackedComponent && trackView) {
      intersectionObserverRef.current = new IntersectionObserver(
        (entries) => {
          const isVisible = entries.some(
            ({ isIntersecting, boundingClientRect }) =>
              isIntersecting && boundingClientRect.height > 0 && boundingClientRect.width > 0
          )

          if (isVisible) {
            addViewedComponent({
              componentName,
              componentClass,
              componentRank,
              panelClass: ga4Parameters.panelClass,
              panelName: ga4Parameters.panelName,
              panelRank: ga4Parameters.panelRank,
              resourceId,
              viewedAt: Date.now(),
              reported: false,
            })
            // The component is tracked only once, so stop observing after the first view.
            intersectionObserverRef.current?.disconnect()
          }
        },
        { threshold: INTERSECTION_THRESHOLD }
      )
      intersectionObserverRef.current.observe(trackedComponent)
    } else if (!trackedComponent && !hasAttachedRef.current && trackView && IS_DEV) {
      console.warn(
        `[shared-ga4] useGA4ComponentDispatcher: trackView is enabled for "${componentName}" but no element is attached. ` +
          'Make sure the returned `ref` is assigned to the component you want to track.'
      )
    }

    return () => {
      intersectionObserverRef.current?.disconnect()
    }
  }, [
    addViewedComponent,
    trackedComponent,
    componentName,
    componentClass,
    componentRank,
    resourceId,
    trackView,
    ga4Parameters.panelClass,
    ga4Parameters.panelName,
    ga4Parameters.panelRank,
  ])

  const send = useCallback(
    (event: GA4Event) => {
      event.ga4_component_name = componentName
      setIfDefined(event, 'ga4_component_class', componentClass)
      setIfDefined(event, 'ga4_component_rank', componentRank)
      setIfDefined(event, 'ga4_resource_id', resourceId)
      setPanelParameters(event, ga4Parameters)
      sendGA4Event(event)
    },
    [componentName, componentClass, componentRank, resourceId, sendGA4Event, ga4Parameters]
  )

  return useMemo(() => ({ ref, send }), [ref, send])
}
